import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { useOddball } from '../midi-context';
import type { CCUpdateEvent, GestureEvent, TapEvent, ShakeEvent, TwistEvent } from '../../src/types';
import { OddballCC } from '../../src/types';

export interface CCValues {
  move: number;
  spin: number;
  freeFall: number;
  orientX: number;
  orientY: number;
  orientZ: number;
  energy: number;
}

const INITIAL_CC: CCValues = {
  move: 0,
  spin: 0,
  freeFall: 0,
  orientX: 64,
  orientY: 64,
  orientZ: 64,
  energy: 0,
};

export function useCCValues(): CCValues {
  const { parser } = useOddball();
  const valuesRef = useRef<CCValues>({ ...INITIAL_CC });
  const [, setTick] = useState(0);
  const rafRef = useRef(0);
  const dirtyRef = useRef(false);

  useEffect(() => {
    const onCC = (evt: CCUpdateEvent) => {
      const v = valuesRef.current;
      switch (evt.cc) {
        case OddballCC.MoveSmoothed: v.move = evt.value; break;
        case OddballCC.SpinSmoothed: v.spin = evt.value; break;
        case OddballCC.FreeFall: v.freeFall = evt.value; break;
        case OddballCC.OrientX: v.orientX = evt.value; break;
        case OddballCC.OrientY: v.orientY = evt.value; break;
        case OddballCC.OrientZ: v.orientZ = evt.value; break;
        case OddballCC.Energy: v.energy = evt.value; break;
      }
      dirtyRef.current = true;
    };

    // Batch re-renders via rAF
    const tick = () => {
      if (dirtyRef.current) {
        dirtyRef.current = false;
        setTick((n) => n + 1);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    parser.on('cc', onCC);
    return () => {
      parser.off('cc', onCC);
      cancelAnimationFrame(rafRef.current);
    };
  }, [parser]);

  return valuesRef.current;
}

export function useGesture<T extends GestureEvent['type']>(
  type: T,
): Extract<GestureEvent, { type: T }> | null {
  const { detector } = useOddball();
  const [latest, setLatest] = useState<Extract<GestureEvent, { type: T }> | null>(null);

  useEffect(() => {
    const handler = (evt: any) => setLatest(evt);
    detector.on(type as any, handler);
    return () => { detector.off(type as any, handler); };
  }, [detector, type]);

  return latest;
}

export interface NoteFlash {
  type: 'tap' | 'shake' | 'twist';
  velocity: number;
  timestamp: number;
}

export function useNoteFlashes(): NoteFlash | null {
  const { parser } = useOddball();
  const [flash, setFlash] = useState<NoteFlash | null>(null);

  useEffect(() => {
    const onTap = (e: TapEvent) => setFlash({ type: 'tap', velocity: e.velocity, timestamp: e.timestamp });
    const onShake = (e: ShakeEvent) => setFlash({ type: 'shake', velocity: e.velocity, timestamp: e.timestamp });
    const onTwist = (e: TwistEvent) => setFlash({ type: 'twist', velocity: e.velocity, timestamp: e.timestamp });

    parser.on('tap', onTap);
    parser.on('shake', onShake);
    parser.on('twist', onTwist);
    return () => {
      parser.off('tap', onTap);
      parser.off('shake', onShake);
      parser.off('twist', onTwist);
    };
  }, [parser]);

  return flash;
}

export function useCCHistory(maxLen = 90): Record<keyof CCValues, number[]> {
  const { parser } = useOddball();
  const histRef = useRef<Record<keyof CCValues, number[]>>({
    move: [], spin: [], freeFall: [], orientX: [], orientY: [], orientZ: [], energy: [],
  });
  const [, setTick] = useState(0);
  const rafRef = useRef(0);
  const dirtyRef = useRef(false);

  useEffect(() => {
    const ccKeyMap: Record<number, keyof CCValues> = {
      [OddballCC.MoveSmoothed]: 'move',
      [OddballCC.SpinSmoothed]: 'spin',
      [OddballCC.FreeFall]: 'freeFall',
      [OddballCC.OrientX]: 'orientX',
      [OddballCC.OrientY]: 'orientY',
      [OddballCC.OrientZ]: 'orientZ',
      [OddballCC.Energy]: 'energy',
    };

    const onCC = (evt: CCUpdateEvent) => {
      const key = ccKeyMap[evt.cc];
      if (key) {
        const arr = histRef.current[key];
        arr.push(evt.value);
        if (arr.length > maxLen) arr.shift();
        dirtyRef.current = true;
      }
    };

    const tick = () => {
      if (dirtyRef.current) {
        dirtyRef.current = false;
        setTick((n) => n + 1);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    parser.on('cc', onCC);
    return () => {
      parser.off('cc', onCC);
      cancelAnimationFrame(rafRef.current);
    };
  }, [parser, maxLen]);

  return histRef.current;
}

export function useGestureLog(maxLen = 100): { events: GestureEvent[]; clear: () => void } {
  const { detector } = useOddball();
  const eventsRef = useRef<GestureEvent[]>([]);
  const [, setTick] = useState(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    const handler = (evt: GestureEvent) => {
      if (pausedRef.current) return;
      eventsRef.current = [evt, ...eventsRef.current].slice(0, maxLen);
      setTick((n) => n + 1);
    };
    detector.on('gesture', handler);
    return () => { detector.off('gesture', handler); };
  }, [detector, maxLen]);

  const clear = useCallback(() => {
    eventsRef.current = [];
    setTick((n) => n + 1);
  }, []);

  return { events: eventsRef.current, clear };
}
